import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { useAuthContext } from '@/contexts/AuthContext';
import { useDadosPessoais } from '@/hooks/useDadosPessoais';

const orNull = (v: string) => {
  const t = v.trim();
  return t === '' ? null : t;
};

export function DadosPessoaisCard() {
  const { t } = useTranslation();
  const { profile, user } = useAuthContext();
  const { salvar, isSaving } = useDadosPessoais();

  const [telefone, setTelefone] = useState(profile?.telefone ?? '');
  const [morada, setMorada] = useState(profile?.morada ?? '');
  const [codigoPostal, setCodigoPostal] = useState(profile?.codigo_postal ?? '');
  const [cidade, setCidade] = useState(profile?.cidade ?? '');
  const [pais, setPais] = useState(profile?.pais ?? '');

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    salvar({
      telefone: orNull(telefone),
      morada: orNull(morada),
      codigoPostal: orNull(codigoPostal),
      cidade: orNull(cidade),
      pais: orNull(pais),
    });
  };

  return (
    <form className="ow-card" onSubmit={onSubmit}>
      <div className="px-6 pt-5 pb-3">
        <h3 className="text-base font-bold tracking-tight">{t('personalDataForm.title')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{t('personalDataForm.description')}</p>
      </div>
      <div className="px-6 pb-5 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t('common.name')}</Label>
          <Input value={profile?.nome ?? ''} disabled readOnly />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t('common.email')}</Label>
          <Input value={user?.email ?? ''} disabled readOnly />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dp-telefone" className="text-xs text-muted-foreground">{t('common.phone')}</Label>
          <PhoneInput id="dp-telefone" value={telefone} onChange={setTelefone} defaultCountry="PT" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dp-morada" className="text-xs text-muted-foreground">{t('personalDataForm.address')}</Label>
          <Input
            id="dp-morada"
            value={morada}
            onChange={(e) => setMorada(e.target.value)}
            placeholder={t('personalDataForm.addressPlaceholder')}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dp-codigo-postal" className="text-xs text-muted-foreground">{t('personalDataForm.postalCode')}</Label>
          <Input id="dp-codigo-postal" value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dp-cidade" className="text-xs text-muted-foreground">{t('personalDataForm.city')}</Label>
          <Input id="dp-cidade" value={cidade} onChange={(e) => setCidade(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dp-pais" className="text-xs text-muted-foreground">{t('personalDataForm.country')}</Label>
          <Input id="dp-pais" value={pais} onChange={(e) => setPais(e.target.value)} />
        </div>
      </div>
      <div className="px-6 pb-5">
        <Button type="submit" disabled={isSaving}>
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
